import { useMemo } from 'react'
import type { ConditionSummaryContext, ConditionSummaryContributor, ConditionSummaryPart } from '@pushly/aqe'
import { type CountInWindowConfig } from './count-in-window-input'
import { COUNT_IN_WINDOW_SURFACES } from './count-in-window-surfaces'
import { frequencyWindowConfig } from './frequency-window-input'
import { JOURNEY_SELECTION_TYPES } from './journey-status-input'
import { allStatusesSelected, deriveJourneyState, JOURNEY_STATUSES, JOURNEY_TRIGGERS } from './journey-status-tree'
import { describeNotification } from './notification-id-clicked-input'
import { describePrompt } from './prompt-picker-input'
import { useSegmentValueInputContext, type JourneyOption, type PromptOption } from './segment-value-input-context'
import { readTermSetAsStrings } from './term-set-value'

/**
 * How a collapsed condition row reads a value one of segmentation's own controls authored.
 *
 * The builder can only summarise a value it understands the shape of, which for these controls is
 * nothing useful: a journey-status condition's whole meaning is a nested rule tree it can do no
 * better than count, and a count-in-a-window condition keeps its window and its expression in meta
 * the generic reading never looks at. Each contributor here states the same choices its own control
 * shows, in the same words, so a reader recognises a collapsed row as the row they edited.
 *
 * A contributor is a plain function called with the condition alone — the collapsed row does not
 * mount the control, so there is nothing to ask. Anything a summary needs from outside the
 * condition is closed over when the registry is built, which is why the registry is built from
 * context by {@link useSegmentValueSummaries} rather than stated as a module constant: a journey's
 * name, the domain's currency and a notification's title all arrive after the first render, and a
 * summary has to re-read as they land.
 *
 * None of this reaches the stored document. A contributor is consulted only to draw a collapsed
 * row, and returns display strings.
 */

// ─── Reading a row as a sentence ─────────────────────────────────────────────

/**
 * The words that introduce a token on a collapsed row.
 *
 * Deliberately *not* the editor's own field labels. Those name the controls in a form, where
 * "Keyword", "Time window" and "Operator" each sit above their input and read correctly. Reused
 * mid-sentence they produced `has Trigger [X] Operator [is exactly] Status [A] [B]` — a dump of the
 * form's field names, with "Operator" leaking an internal word to the reader.
 *
 * Where a connective wraps a label the editor owns, it wraps rather than restates it: renaming
 * "Page tag" in the surface table still flows through to "where page tag".
 */
const PROSE = {
    /** Introduces the window a count is taken over. Window labels read "7 Days", "1 Day". */
    inTheLast: 'in the last',
    /**
     * Introduces the statuses a journey condition matches, carrying its own equality.
     *
     * The equality has to agree with how many statuses follow it, because of how the tree joins the
     * rules: under `eq` they are an `or`, so several are a set the membership's status is drawn from,
     * while under `neq` they are an `and`, which excludes each one. "is exactly" over three of them
     * read as a single compound value rather than as any one of the three.
     *
     * The word "status" is not repeated here. The field this reads for is named "Journey Status", so
     * the row already said it, and a predicate that says it again reads as a second subject.
     */
    statusIs: (operator: 'eq' | 'neq', count: number) => {
        if (count <= 1) return operator === 'neq' ? 'is not' : 'is'
        return operator === 'neq' ? 'is none of' : 'is any of'
    },
    /**
     * Introduces the journey a status match is scoped to, and the two verbs for a condition that
     * asserts nothing about status.
     *
     * These carry the verb themselves. A nested condition has no operator of its own, so the row
     * only supplies one where nothing describes the condition better — which means the reading owns
     * how the sentence starts, and can say "has entered" where a status match says "is".
     */
    forScope: (lead: string) => ['for', lead].filter(Boolean).join(' '),
    hasEntered: (lead: string) => ['has entered', lead].filter(Boolean).join(' '),
    hasNotEntered: (lead: string) => ['has not entered', lead].filter(Boolean).join(' '),
    /** Names the kind of journey a scope is, matching the words the editor's own selector uses. */
    journey: 'Journey',
    triggeredJourney: 'Journey with Trigger',
} as const

/**
 * A count and the unit it counts in, agreeing in number.
 *
 * "1 times" is the reason this exists. The plural is the stored label, so the singular is derived
 * from it rather than declared twice — a surface that renames its unit cannot end up with a plural
 * that pluralizes and a singular that does not.
 */
function countWithUnit(value: number | string, plural: string | null, prefix: string): string {
    if (plural === null) return `${prefix}${value}`

    const singular = Number(value) === 1 && plural.endsWith('s') ? plural.slice(0, -1) : plural
    return `${prefix}${value} ${singular}`
}

// ─── Journey status ──────────────────────────────────────────────────────────

/**
 * Reads a journey-status condition as the four choices its control shows: which kind of journey
 * match, the journey or trigger it names, the equality, and the statuses.
 *
 * The statuses lead and the journey trails as a qualifier, so the row reads the way every other row
 * in the builder does — the field, then what is being asserted about it, then what it is being
 * compared against. Naming the journey first put the scope between the field and its predicate, and
 * "Journey Status has journey X status is any of Active" reads as a dump of the editor's controls
 * rather than as a claim about a subscriber.
 *
 * A condition with no status named is not asserting anything about status: it matches on having been
 * in the journey at all, whatever became of it. That is a different sentence, not the same one with
 * a part missing, so it is read with its own verb and no status clause.
 *
 * The journey resolves to a name from the loaded list, and an id that resolves to none is read as
 * itself — a journey since deleted is still what the condition targets, and a row that dropped it
 * would describe a segment nobody saved. A choice not yet made contributes no token at all: a
 * labelled token with nothing in it reads as a journey whose name is blank rather than as a journey
 * not picked, and the row already says which rule is unfilled through the error against it.
 */
export function createJourneyStatusSummary(journeys: JourneyOption[]): ConditionSummaryContributor {
    const names = new Map(journeys.map((journey) => [journey.id, journey.name]))

    return ({ value }: ConditionSummaryContext) => {
        const state = deriveJourneyState(value)
        const scope = readScope(state, names)

        // A negated specific journey with every status named is the "not entered" shape: the stored
        // tree holds the journey rule alone, carrying the negation, and no status rule at all. Read
        // as a status match it would say the opposite of what the segment matches — that the reader
        // is in the journey — and attribute the negation to statuses the tree does not mention.
        if (state.selectionType === 'specific' && state.operator === 'neq' && allStatusesSelected(state.statuses)) {
            return scope === null ? [] : [{ label: PROSE.hasNotEntered(scope.lead), value: scope.value }]
        }

        if (state.statuses.length === 0) {
            return scope === null ? [] : [{ label: PROSE.hasEntered(scope.lead), value: scope.value }]
        }

        // The equality is folded into the words that introduce the statuses rather than standing as
        // a token of its own. On its own it needed a name, and the only name for it was "Operator".
        const statusLabel = PROSE.statusIs(state.operator, state.statuses.length)

        // The statuses are a set, so they are read as one: the words lead the first and the rest
        // follow as further values under it rather than repeating once per status.
        const parts: ConditionSummaryPart[] = state.statuses.map((status, index) => {
            const option = JOURNEY_STATUSES.find((candidate) => candidate.value === status)
            const label = option?.label ?? status
            return index === 0 ? { label: statusLabel, value: label } : { value: label }
        })

        if (scope !== null) {
            parts.push({ label: PROSE.forScope(scope.lead), value: scope.value })
        }

        return parts
    }
}

/**
 * What a journey-status condition is scoped to, as the words that introduce it and the value itself.
 *
 * Split so the same scope reads correctly after any of the leading phrases — "for", "has entered",
 * "has not entered" — instead of each phrase carrying its own copy of the three selection types.
 * Only the value is drawn as a token, because only the value is one: "Journey with Trigger" names
 * what follows it the way every other introducing phrase on a row does.
 *
 * `null` where the reader has not picked the journey or trigger yet. The row is reported as
 * incomplete, and a scope drawn with nothing in it would read as one whose name is blank.
 */
function readScope(
    state: ReturnType<typeof deriveJourneyState>,
    names: Map<number, string>,
): { lead: string; value: string } | null {
    if (state.selectionType === 'specific') {
        if (state.journeyId === undefined) return null
        return { lead: PROSE.journey, value: names.get(state.journeyId) ?? `#${state.journeyId}` }
    }

    if (state.selectionType === 'triggered') {
        const trigger = JOURNEY_TRIGGERS.find((option) => option.value === state.triggerType)
        const label = trigger?.label ?? state.triggerType
        if (label === undefined) return null
        return { lead: PROSE.triggeredJourney, value: label }
    }

    // "Any Journey" is the whole of what this condition is scoped to, so it needs no phrase of its
    // own in front of it — the leading word supplies one.
    return { lead: '', value: JOURNEY_SELECTION_TYPES.find((option) => option.value === 'any')?.label ?? '' }
}

// ─── Counts in a window ──────────────────────────────────────────────────────

/**
 * Reads a count-in-a-window condition as the count, whatever narrows it, and the window it is
 * counted over.
 *
 * The count carries no words of its own: the row's own operator already precedes it ("is at least
 * 8 times"), and naming it again would only repeat what the reader can see. Its units are the
 * surface's — the trailing `times`, or the currency symbol a money surface puts ahead of an amount
 * — because a bare number is the one thing this row was reported for.
 *
 * An absent window is stated as absent rather than as this surface's default. The engine counts
 * over the subscriber's whole history where the condition names no window, so naming one would
 * describe a narrower audience than the segment matches. A stored window the surface no longer
 * offers is read as the token it holds, for the same reason its control still shows it.
 */
export function summariseCountInWindow(
    config: CountInWindowConfig,
    { value, meta }: ConditionSummaryContext,
): ConditionSummaryPart[] {
    const parts: ConditionSummaryPart[] = []

    // Ordered as the sentence reads, not as the form is laid out. The count and the window are one
    // measurement — "at least 1 time in the last 7 days" — so they stay together, and whatever
    // narrows that measurement trails it. Interleaving them split the measurement in half:
    // "at least 1 time where page tag zero-margin in the last 7 days".
    if (typeof value === 'number' || (typeof value === 'string' && value !== '')) {
        parts.push({ value: countWithUnit(value, config.valueLabel ?? null, config.valuePrefix ?? '') })
    }

    const scope = meta.scope ?? ''
    if (scope !== '') {
        const window = config.windowOptions.find((option) => option.value === scope)
        parts.push({ label: PROSE.inTheLast, value: window?.label ?? scope })
    }

    const expression = meta.expression ?? ''
    if (config.expression.kind !== 'none' && expression !== '') {
        parts.push({ label: config.expression.summaryLead, value: expression })
    }

    return parts
}

/**
 * The frequency surfaces' summary, which needs the currency before it can read an amount of money
 * as one. Every other count surface's configuration is fixed and is read straight from the table.
 */
export function createFrequencyWindowSummary(currencySymbol: string): ConditionSummaryContributor {
    return (context) => summariseCountInWindow(frequencyWindowConfig(context.field.key, currencySymbol), context)
}

// ─── Notifications clicked ───────────────────────────────────────────────────

/**
 * Reads a notification-clicked condition as the notifications it names, each in the same form the
 * control lists them in.
 *
 * Both stored shapes are read: the bare scalar a one-notification condition holds and the list.
 * A title is shown where one has been resolved; anything else reads as its bare id, which is what a
 * row shows before the control has ever been opened on it and what it keeps showing for a
 * notification the account can no longer return.
 */
export function createNotificationIdClickedSummary(titles: Record<string, string>): ConditionSummaryContributor {
    return ({ value }) =>
        readTermSetAsStrings(value).map((id) => ({ value: describeNotification(id, titles[id]) }))
}

// ─── Subscription prompt ─────────────────────────────────────────────────────

/**
 * Reads a prompt condition as the prompt it targets, in the same form the picker lists it.
 *
 * The condition stores a bare id, so without this the row showed `143403` — a number identifying
 * nothing to the reader, and the one case where opening the editor was the only way to learn what a
 * condition matched. An id the loaded list does not hold still reads as itself: a deleted prompt is
 * still what the segment targets, and a row that blanked it would describe a segment nobody saved.
 */
export function createPromptPickerSummary(prompts: PromptOption[]): ConditionSummaryContributor {
    const names = new Map(prompts.map((prompt) => [prompt.id, prompt.name]))

    return ({ value }) => {
        if (typeof value !== 'number' && typeof value !== 'string') return null
        if (value === '') return null

        return [{ value: describePrompt(value, names.get(Number(value))) }]
    }
}

// ─── The registry ────────────────────────────────────────────────────────────

/**
 * The summary contributors segmentation supplies, keyed the way the builder resolves them:
 * `field.inputKey ?? field.type`, the same key its value input is registered under.
 *
 * Rebuilt whenever a vocabulary the summaries read arrives, which is what makes a collapsed row
 * name a journey rather than an id once the journey list lands. A key with no entry here keeps the
 * builder's own generic summary, which is what every field the catalog adds gets until someone
 * decides it reads badly.
 */
export function useSegmentValueSummaries(): Record<string, ConditionSummaryContributor> {
    const { journeys, currencySymbol, notificationTitles, prompts } = useSegmentValueInputContext()

    return useMemo(() => {
        const summaries: Record<string, ConditionSummaryContributor> = {
            'frequency-window': createFrequencyWindowSummary(currencySymbol),
            'journey-status': createJourneyStatusSummary(journeys),
            'notification-id-clicked': createNotificationIdClickedSummary(notificationTitles ?? {}),
            'prompt-picker': createPromptPickerSummary(prompts),
        }

        for (const [inputKey, config] of Object.entries(COUNT_IN_WINDOW_SURFACES)) {
            summaries[inputKey] = (context) => summariseCountInWindow(config, context)
        }

        return summaries
    }, [journeys, currencySymbol, notificationTitles, prompts])
}
